/**
 * FEAT-096 / S-403 — the Newsletter screen's settings form, as data.
 *
 * Numbers are held as the strings the inputs produce and parsed only here, so
 * "2.5" or "" can be refused with a message instead of silently becoming NaN.
 * Only changed fields are sent: the API's audit entry then records exactly
 * what was edited, and PATCH merges deep_dives key by key.
 *
 * Distribution and sources are not part of this form — distribution is
 * read-only (plan Decision 4) and sources have their own full-list editor (S-404).
 */

import type { NewsletterConfig } from "@/types/newsletter";

export interface NewsletterSettingsDraft {
  objective: string;
  body: string;
  output_format: "html" | "markdown";
  periodicity_days: string;
  max_items: string;
  min_items: string;
  deep_dives: {
    enabled: boolean;
    count: string;
    candidates_count: string;
    editorial_gate: boolean;
    auto_advance: boolean;
  };
}

export interface NewsletterSettingsPatch {
  objective?: string;
  body?: string;
  output_format?: "html" | "markdown";
  periodicity_days?: number;
  max_items?: number;
  min_items?: number;
  deep_dives?: {
    enabled?: boolean;
    count?: number;
    candidates_count?: number | null;
    editorial_gate?: boolean;
    auto_advance?: boolean;
  };
}

export type BuildPatchResult =
  | { ok: true; patch: NewsletterSettingsPatch }
  | { ok: false; errors: string[] };

export function draftFromConfig(c: NewsletterConfig): NewsletterSettingsDraft {
  return {
    objective: c.objective,
    body: c.body,
    output_format: c.output_format,
    periodicity_days: String(c.periodicity_days),
    max_items: String(c.max_items),
    min_items: String(c.min_items),
    deep_dives: {
      enabled: c.deep_dives.enabled,
      count: String(c.deep_dives.count),
      candidates_count: c.deep_dives.candidates_count == null ? "" : String(c.deep_dives.candidates_count),
      editorial_gate: c.deep_dives.editorial_gate,
      auto_advance: c.deep_dives.auto_advance,
    },
  };
}

function positiveInt(raw: string, label: string, errors: string[]): number | undefined {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
    errors.push(`${label} must be a whole number of at least 1.`);
    return undefined;
  }
  return Number(trimmed);
}

export function buildSettingsPatch(
  config: NewsletterConfig,
  draft: NewsletterSettingsDraft
): BuildPatchResult {
  const errors: string[] = [];

  if (!draft.objective.trim()) errors.push("Objective can't be empty — the curator ranks items against it.");
  const periodicity = positiveInt(draft.periodicity_days, "Collection window", errors);
  const maxItems = positiveInt(draft.max_items, "Maximum items", errors);
  const minItems = positiveInt(draft.min_items, "Minimum items", errors);
  const ddCount = positiveInt(draft.deep_dives.count, "Deep dives per edition", errors);
  const candidates =
    draft.deep_dives.candidates_count.trim() === ""
      ? null
      : positiveInt(draft.deep_dives.candidates_count, "Candidates", errors);

  if (minItems !== undefined && maxItems !== undefined && minItems > maxItems) {
    errors.push(`Minimum items (${minItems}) can't be above the maximum (${maxItems}).`);
  }
  // Review fix — matches the API's own merged-row check. The draft always
  // carries both fields' full current values (draftFromConfig seeds both),
  // so comparing them directly is enough here — no merge needed the way the
  // API needs one against a partial PATCH.
  if (ddCount !== undefined && candidates !== null && candidates !== undefined && ddCount > candidates) {
    errors.push(`Deep dives per edition (${ddCount}) can't be more than the number of candidates (${candidates}).`);
  }
  if (errors.length > 0) return { ok: false, errors };

  const patch: NewsletterSettingsPatch = {};
  if (draft.objective !== config.objective) patch.objective = draft.objective;
  if (draft.body !== config.body) patch.body = draft.body;
  if (draft.output_format !== config.output_format) patch.output_format = draft.output_format;
  if (periodicity !== config.periodicity_days) patch.periodicity_days = periodicity;
  if (maxItems !== config.max_items) patch.max_items = maxItems;
  if (minItems !== config.min_items) patch.min_items = minItems;

  const d = config.deep_dives;
  const dd: NonNullable<NewsletterSettingsPatch["deep_dives"]> = {};
  if (draft.deep_dives.enabled !== d.enabled) dd.enabled = draft.deep_dives.enabled;
  if (ddCount !== d.count) dd.count = ddCount;
  if (candidates !== (d.candidates_count ?? null)) dd.candidates_count = candidates ?? null;
  if (draft.deep_dives.editorial_gate !== d.editorial_gate) dd.editorial_gate = draft.deep_dives.editorial_gate;
  if (draft.deep_dives.auto_advance !== d.auto_advance) dd.auto_advance = draft.deep_dives.auto_advance;
  if (Object.keys(dd).length > 0) patch.deep_dives = dd;

  return { ok: true, patch };
}
